import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/dashboard/DashboardIcons';
import { API, auth, money } from '../organization/finance/shared';
import { PERIOD_OPTIONS } from './periodOptions';

const StatCard = ({ icon, label, value, testId, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl ${accent || 'bg-orange-50 text-orange-600'}`}>
      <Icon name={icon} className="h-4.5 w-4.5" />
    </div>
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    <p className="mt-0.5 text-xl font-black text-slate-900">{value}</p>
  </div>
);

export default function VenueOwnerHomePage() {
  const { venues } = useOutletContext();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('this_month');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    try {
      const res = await axios.get(`${API}/venues/owner/dashboard-summary`, { ...auth(), params: { period: p } });
      setSummary(res.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  if (loading) return <div className="h-40 animate-pulse rounded-2xl bg-slate-100" data-testid="venue-owner-home-loading" />;

  return (
    <div className="space-y-6" data-testid="venue-owner-home-page">
      {venues.length === 0 && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm font-semibold text-orange-800" data-testid="venue-owner-home-no-venues-alert">
          لا يوجد لديك ملاعب حتى الآن — أضف أول ملعب لتبدأ استقبال الحجوزات.
          <button onClick={() => navigate('/venue-owner-dashboard/venues')} className="mr-2 mt-2 block rounded-lg bg-orange-600 px-4 py-2 text-xs font-bold text-white sm:mt-0 sm:inline-block" data-testid="venue-owner-home-add-venue-cta">إضافة ملعب</button>
        </div>
      )}

      {summary?.pending_requests_count > 0 && (
        <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="venue-owner-home-pending-alert">
          <p className="text-sm font-bold text-amber-800">لديك {summary.pending_requests_count} طلب حجز بانتظار المراجعة</p>
          <button onClick={() => navigate('/venue-owner-dashboard/bookings')} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="venue-owner-home-review-requests-btn">مراجعة الطلبات</button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <StatCard icon="calendar" label="حجوزات اليوم" value={summary?.today_bookings_count ?? 0} testId="venue-owner-stat-today" />
        <StatCard icon="clock" label="الحجوزات القادمة (7 أيام)" value={summary?.upcoming_bookings_count ?? 0} testId="venue-owner-stat-upcoming" />
        <StatCard icon="inbox" label="طلبات قيد المراجعة" value={summary?.pending_requests_count ?? 0} testId="venue-owner-stat-pending" accent="bg-amber-50 text-amber-600" />
        <StatCard icon="check" label="حجوزات مؤكدة" value={summary?.confirmed_bookings_count ?? 0} testId="venue-owner-stat-confirmed" accent="bg-emerald-50 text-emerald-600" />
        <StatCard icon="chart" label="نسبة الإشغال (تقديرية)" value={`${summary?.occupancy_percent ?? 0}%`} testId="venue-owner-stat-occupancy" />
        <StatCard icon="field" label="عدد الملاعب" value={summary?.venues_count ?? 0} testId="venue-owner-stat-venues-count" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="venue-owner-home-revenue-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-slate-500">الإيرادات المتوقعة</p>
            <p className="mt-0.5 text-2xl font-black text-emerald-700" data-testid="venue-owner-home-revenue-value">{money(summary?.expected_revenue ?? 0)}</p>
          </div>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700" data-testid="venue-owner-home-period-select">
            {PERIOD_OPTIONS.filter((p) => p.value !== 'custom').map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {summary?.nearest_booking && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="venue-owner-home-nearest-booking">
          <p className="mb-2 text-xs font-semibold text-slate-500">أقرب حجز</p>
          <p className="text-sm font-bold text-slate-900">{summary.nearest_booking.venue_name} — {summary.nearest_booking.date} من {summary.nearest_booking.start_time} إلى {summary.nearest_booking.end_time}</p>
          <p className="mt-1 text-xs text-slate-500">{summary.nearest_booking.customer_name || 'بلا اسم عميل'} • {summary.nearest_booking.customer_phone || '—'}</p>
        </div>
      )}

      <div>
        <p className="mb-3 text-sm font-bold text-slate-700">إجراءات سريعة</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <button onClick={() => navigate('/venue-owner-dashboard/bookings')} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center transition-colors hover:border-orange-300 hover:bg-orange-50" data-testid="venue-owner-quick-action-manual-booking">
            <Icon name="plus" className="h-5 w-5 text-orange-600" /><span className="text-xs font-bold text-slate-700">حجز يدوي</span>
          </button>
          <button onClick={() => navigate('/venue-owner-dashboard/venues')} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center transition-colors hover:border-orange-300 hover:bg-orange-50" data-testid="venue-owner-quick-action-add-venue">
            <Icon name="field" className="h-5 w-5 text-orange-600" /><span className="text-xs font-bold text-slate-700">إضافة ملعب</span>
          </button>
          <button onClick={() => navigate('/venue-owner-dashboard/bookings')} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center transition-colors hover:border-orange-300 hover:bg-orange-50" data-testid="venue-owner-quick-action-block-time">
            <Icon name="lock" className="h-5 w-5 text-orange-600" /><span className="text-xs font-bold text-slate-700">حظر وقت</span>
          </button>
          <button onClick={() => navigate('/venue-owner-dashboard/calendar')} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-center transition-colors hover:border-orange-300 hover:bg-orange-50" data-testid="venue-owner-quick-action-calendar">
            <Icon name="calendar" className="h-5 w-5 text-orange-600" /><span className="text-xs font-bold text-slate-700">عرض التقويم</span>
          </button>
        </div>
      </div>
    </div>
  );
}
